import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ShieldCheck, Network, Award, TrendingUp } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/data";

const HERO = "https://images.pexels.com/photos/2587007/pexels-photo-2587007.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80";
const SAV = "https://images.pexels.com/photos/31207738/pexels-photo-31207738.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80";

const values = [
  { icon: Globe2, t: "UK market expertise", d: "London-based team with deep knowledge of the British travel trade, regulators and consortia." },
  { icon: ShieldCheck, t: "Operational discipline", d: "Robust controls, reporting and reconciliation that airlines demand — matched to UK compliance standards." },
  { icon: Network, t: "Distribution reach", d: "Active in every major GDS, NDC partner, OTA and metasearch surface from the UK point of sale." },
  { icon: Award, t: "Brand stewardship", d: "We protect and amplify your airline brand through co-ordinated marketing, PR and trade engagement." },
];

const reasons = [
  "Single point of accountability for UK & Ireland",
  "Lower fixed-cost market entry vs. owned operation",
  "Pre-existing relationships with TMCs and consortia",
  "Bilingual UK-based contact centre",
  "Transparent revenue & cost reporting",
  "Capacity to scale across multi-airline portfolio",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Simba Travel — UK Airline GSA for Air Zimbabwe"
        description="Simba Travel is a London-based General Sales Agent representing Air Zimbabwe in the UK & Ireland. Learn about our mission, market expertise, distribution reach and why airlines partner with us."
      />
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[480px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="Airline operations" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/40" />
        <div className="relative container-edge pb-16 text-linen">
          <div className="overline text-ochre mb-4">About Simba Travel</div>
          <h1 className="editorial-h1 max-w-3xl">The British home of African aviation.</h1>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="overline text-terracotta mb-4">Company overview</div>
            <h2 className="editorial-h2">Trading as Simba Travel — operating as {COMPANY.legalName}.</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:pt-4">
            <p className="text-lg leading-relaxed text-ink/80 font-light">
              {COMPANY.legalName} (Company Registration {COMPANY.registration}) is a private UK-registered company headquartered in London. Trading as Simba Travel, the business specialises in airline General Sales Agency, representation and travel-trade support.
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted2">
              Our initial mandate is the representation of Air Zimbabwe in the United Kingdom and Ireland. Our infrastructure — BSP settlement, GDS connectivity, contact-centre platform and UK compliance controls — is purpose-built to onboard additional airline partners as the business grows.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="section-pad bg-sand">
        <div className="container-edge grid grid-cols-1 md:grid-cols-2 gap-12">
          <Reveal className="border-l-2 border-terracotta pl-8">
            <div className="overline text-terracotta">Mission</div>
            <h3 className="editorial-h3 mt-4 mb-6">
              To make Africa effortlessly accessible from the United Kingdom.
            </h3>
            <p className="text-muted2 leading-relaxed">
              We do this by representing world-class African airlines in the UK market — providing the commercial, distribution and service infrastructure that British travellers, travel agents and corporates expect.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="border-l-2 border-midnight pl-8">
            <div className="overline text-midnight">Vision</div>
            <h3 className="editorial-h3 mt-4 mb-6">
              To be the leading airline GSA partner in the UK for African and emerging-market carriers.
            </h3>
            <p className="text-muted2 leading-relaxed">
              Building a multi-airline representation portfolio rooted in commercial discipline, trade integrity and authentic regional understanding.
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-pad bg-linen">
        <div className="container-edge">
          <SectionHeading overline="What we stand for" title="Four operating principles." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <Reveal key={v.t} className="bg-sand p-8 h-full" data-testid={`value-${v.t}`}>
                <v.icon className="w-7 h-7 stroke-1 text-terracotta" />
                <div className="font-serif text-xl mt-6">{v.t}</div>
                <p className="text-sm text-muted2 mt-3 leading-relaxed">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="section-pad bg-midnight text-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <img src={SAV} alt="African savannah" className="w-full aspect-[4/3] object-cover" />
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="overline text-ochre mb-4">Airlines partner with us because</div>
            <h2 className="editorial-h2 text-linen">A grown-up GSA for a demanding market.</h2>
            <ul className="mt-8 grid grid-cols-1 gap-3">
              {reasons.map((r) => (
                <li key={r} className="flex items-start gap-3 text-linen/85">
                  <TrendingUp className="w-4 h-4 stroke-1 text-ochre mt-1 flex-shrink-0" />
                  <span className="text-sm leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-accent mt-10" data-testid="about-partner-cta">
              Discuss a partnership <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ENQUIRY */}
      <section className="section-pad bg-sand">
        <div className="container-edge max-w-3xl">
          <EnquiryForm defaultType="airline-partnership" testIdPrefix="about-enquiry" />
        </div>
      </section>
    </div>
  );
}
